import { FORM_ERROR } from 'final-form';
import { useContext } from 'react';
import { Field, Form } from 'react-final-form';
import { api } from 'src/client/api';
import { setDoNotAskToDeleteDevice } from 'src/common/actions/app';
import { dontShowDeleteDeviceConfirmation } from 'src/common/constants/cookies';
import {
  DELETE_DEVICE_BODY_TEXT,
  DELETE_DEVICE_CHECKBOX_LABEL,
  DELETE_DEVICE_CONFIRM_DELETE,
  DELETE_DEVICE_ERROR,
  DELETE_DEVICE_TITLE_REMOVE_DEVICE,
} from 'src/common/constants/localizations/devices';
import { NEVERMIND } from 'src/common/constants/localizations/shared';
import useActions from 'src/common/hooks/useActions';
import { useStores } from 'src/common/providers';
import { LocationAndLocalizationContext } from '../../../providers/LocationAndLocalizationProvider';
import { setCookie } from '../../../utils/cookie';
import InkButton from '../../shared/button/InkButton';
import NakedButton from '../../shared/button/NakedButton';
import CheckBox from '../../shared/form/CheckBox';
import css from '../account-settings.module.scss';

interface DeleteDeviceFormProps {
  deviceId?: number | null;
  closeCallback: () => void;
  handleDeleteError: () => void;
}

interface DeleteDeviceFormValues {
  dontAskAgain: boolean;
}

interface DeleteDeviceFormSubmitError {
  [FORM_ERROR]: string;
}

export function DeleteDeviceForm({
  deviceId,
  closeCallback,
  handleDeleteError,
}: DeleteDeviceFormProps) {
  const actions = useActions<{ setDoNotAskToDeleteDevice: () => void }>({
    setDoNotAskToDeleteDevice,
  });
  const { getLocalizedText } = useContext(LocationAndLocalizationContext);
  const { userStore } = useStores();
  const removeDevice = userStore.removeDevice();

  async function onSubmit({
    dontAskAgain,
  }: DeleteDeviceFormValues): Promise<void | DeleteDeviceFormSubmitError> {
    if (deviceId == null) {
      handleDeleteError();

      return {
        [FORM_ERROR]: getLocalizedText(DELETE_DEVICE_ERROR),
      };
    }

    try {
      await api.user.deleteDevice(deviceId);
      removeDevice(deviceId);

      if (dontAskAgain) {
        setCookie(dontShowDeleteDeviceConfirmation.name);
        actions.setDoNotAskToDeleteDevice();
      }

      closeCallback();
    } catch {
      handleDeleteError();

      return {
        [FORM_ERROR]: getLocalizedText(DELETE_DEVICE_ERROR),
      };
    }
  }

  return (
    <Form
      initialValues={{
        dontAskAgain: false,
      }}
      onSubmit={onSubmit}
    >
      {({ handleSubmit, submitting, submitError }) => (
        <form name="deleteDevice" data-testid="deleteDeviceForm" onSubmit={handleSubmit}>
          <h2 data-testid="headline" className={css.contentTitle}>
            {getLocalizedText(DELETE_DEVICE_TITLE_REMOVE_DEVICE)}
          </h2>
          {submitError && (
            <p data-testid="mainError" className={css.errorField}>
              {submitError}
            </p>
          )}
          <p data-testid="bodyText" className={css.contentBodyText}>
            {getLocalizedText(DELETE_DEVICE_BODY_TEXT)}
          </p>
          <div className={css.checkboxWrapper}>
            <Field
              component={CheckBox}
              customSpanClass={css.rememberMeCheckbox}
              customCheckmarkClass={css.customCheckmarkClass}
              customCheckmarkUncheckedClass={css.customCheckmarkUncheckedClass}
              labelClassName={css.rememberMeLabel}
              type="checkbox"
              id="dontAskAgain"
              dataTestId="dontAskAgain"
              name="dontAskAgain"
              label={getLocalizedText(DELETE_DEVICE_CHECKBOX_LABEL)}
              autoFocus
            />
          </div>
          <div className="row">
            <div className="col-xs-12">
              <InkButton
                id="removeDeviceButton"
                dataTestId="removeDeviceButton"
                type="submit"
                className={css.ctaButton}
                isDisabled={submitting}
                label={getLocalizedText(DELETE_DEVICE_CONFIRM_DELETE)}
              />
            </div>
          </div>
          <div className="row">
            <div className="col-xs-12">
              <NakedButton
                id="cancelButton"
                dataTestId="cancelButton"
                className={css.ctaButton}
                label={getLocalizedText(NEVERMIND)}
                onClick={closeCallback}
              />
            </div>
          </div>
        </form>
      )}
    </Form>
  );
}
